import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import {createSellerMediaApi} from './mediaApi';
import MediaCard, {mediaKind} from './MediaCard';
import MediaDetailsPanel from './MediaDetailsPanel';
import MediaUploadPanel from './MediaUploadPanel';
import './MediaLibraryManager.css';
import ImageCropEditor from './ImageCropEditor';

function matchesAccept(file, accept) {
    if (!accept) return true;
    return accept.split(',').map(value => value.trim()).some(value => {
        if (!value) return false;
        if (value.startsWith('.')) {
            return file.filename?.toLowerCase().endsWith(value.toLowerCase());
        }
        if (value.endsWith('/*')) {
            return file.mimetype?.startsWith(value.slice(0, -1));
        }
        return file.mimetype === value;
    });
}

function updateFile(files, nextFile) {
    return files.map(file => file.uuid === nextFile.uuid ? nextFile : file);
}

const FLASH_TONES = ['success', 'error', 'info'];

export default function MediaLibraryManager({
    variant = 'page',
    isOpen = true,
    title = 'Media Library',
    accept = '',
    selectable = false,
    initialValue = '',
    multiSelect = false,
    selectedIds,
    flash,
    apiClient,
    onSelect,
    onDeselect,
    onCancel,
    onUpload,
    onDelete,
    cropControls = null,
}) {
    const {fetch, API_URL} = useAuth();
    const api = useMemo(
        () => apiClient || createSellerMediaApi({fetch, apiUrl: API_URL}),
        [API_URL, apiClient, fetch],
    );
    const [cropFile, setCropFile] = useState(null);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [kind, setKind] = useState('all');
    const [showUpload, setShowUpload] = useState(false);
    const [selectedValue, setSelectedValue] = useState(initialValue || '');
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');
    const [dismissedFlash, setDismissedFlash] = useState(null);

    const selectedIdSet = useMemo(
        () => new Set((selectedIds || []).map(id => String(id))),
        [selectedIds],
    );
    const isAttached = file => multiSelect && selectedIdSet.has(String(file.id));
    const activeFlash = flash && flash.message && flash !== dismissedFlash ? flash : null;
    const flashTone = activeFlash && FLASH_TONES.includes(activeFlash.tone)
        ? activeFlash.tone
        : 'info';

    const loadFiles = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setFiles(await api.list());
        } catch (loadError) {
            setError(loadError.message);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        if (isOpen) {
            loadFiles();
        }
    }, [isOpen, loadFiles]);

    useEffect(() => {
        setSelectedValue(initialValue || '');
    }, [initialValue]);

    useEffect(() => {
        if (variant !== 'modal' || !isOpen) return undefined;
        const handleKeyDown = event => {
            if (event.key === 'Escape' && !cropFile) {
                onCancel?.();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onCancel, variant, cropFile]);

    if (!isOpen) {
        return null;
    }

    const openDetail = async file => {
        setDetailLoading(true);
        setDetailError('');
        setDetail({file, attachments: [], attachmentCount: 0});
        try {
            setDetail(await api.get(file.uuid));
        } catch (loadError) {
            setDetailError(loadError.message);
        } finally {
            setDetailLoading(false);
        }
    };

    const selectFile = file => {
        setDetail(null);
        if (multiSelect) {
            // A click on an already-attached file toggles it back out of the set.
            if (selectedIdSet.has(String(file.id))) {
                onDeselect?.(file);
            } else {
                onSelect?.(file);
            }
            return;
        }
        setSelectedValue(file.id);
        onSelect?.(file);
    };

    const filteredFiles = files.filter(file => {
        if (!matchesAccept(file, accept)) return false;
        if (kind !== 'all' && mediaKind(file) !== kind) return false;
        if (!search.trim()) return true;
        const haystack = [
            file.title,
            file.filename,
            file.description,
            file.metadata?.altText,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(search.trim().toLowerCase());
    });

    const panel = (
        <div className={`media-system media-system-${variant}`}>
            <header className="media-system-header">
                <div>
                    <span className="media-system-eyebrow">
                        {selectable ? 'Choose a file' : 'Storefront files'}
                    </span>
                    <h2>{title}</h2>
                    <p>Upload, find, edit, rotate, and safely remove reusable media.</p>
                </div>
                <div className="media-system-header-actions">
                    <button
                        type="button"
                        className="button"
                        onClick={() => setShowUpload(value => !value)}
                    >
                        <i className="fas fa-upload" aria-hidden="true"></i>
                        {showUpload ? 'Close uploader' : 'Upload files'}
                    </button>
                    {variant !== 'page' && (
                        <button type="button" className="button secondary" onClick={onCancel}>
                            Cancel
                        </button>
                    )}
                </div>
            </header>

            {showUpload && (
                <MediaUploadPanel
                    accept={accept}
                    api={api}
                    onClose={() => setShowUpload(false)}
                    onUpload={file => {
                        setFiles(current => [
                            file,
                            ...current.filter(item => item.uuid !== file.uuid),
                        ]);
                        onUpload?.(file);
                    }}
                />
            )}

            <div className="media-system-toolbar">
                <label className="media-system-search">
                    <span className="sr-only">Search media</span>
                    <i className="fas fa-search" aria-hidden="true"></i>
                    <input
                        type="search"
                        value={search}
                        placeholder="Search filename, title, or description"
                        onChange={event => setSearch(event.target.value)}
                    />
                </label>
                <label>
                    <span className="sr-only">Filter by file type</span>
                    <select value={kind} onChange={event => setKind(event.target.value)}>
                        <option value="all">All file types</option>
                        <option value="image">Images</option>
                        <option value="video">Videos</option>
                        <option value="audio">Audio</option>
                        <option value="pdf">PDFs</option>
                        <option value="file">Other files</option>
                    </select>
                </label>
                <span className="media-system-count">
                    {filteredFiles.length} {filteredFiles.length === 1 ? 'file' : 'files'}
                </span>
            </div>

            {activeFlash && (
                <div
                    className={`flash ${flashTone} media-system-flash`}
                    role={flashTone === 'error' ? 'alert' : 'status'}
                >
                    <p>{activeFlash.message}</p>
                    <button
                        type="button"
                        className="button small"
                        onClick={() => setDismissedFlash(flash)}
                        aria-label="Dismiss message"
                    >
                        Dismiss
                    </button>
                </div>
            )}
            {error && (
                <div className="flash error" role="alert">
                    <p>{error}</p>
                    <button type="button" className="button small" onClick={loadFiles}>
                        Try again
                    </button>
                </div>
            )}
            {loading && <p className="media-system-status">Loading media library...</p>}
            {!loading && !error && filteredFiles.length === 0 && (
                <div className="media-system-empty">
                    <i className="far fa-images" aria-hidden="true"></i>
                    <h3>
                        {files.length ? 'No files match these filters' : 'Your library is empty'}
                    </h3>
                    <p>
                        {files.length
                            ? 'Try a different search or file type.'
                            : 'Upload a file to get started.'}
                    </p>
                </div>
            )}
            {!loading && filteredFiles.length > 0 && (
                <div className="media-system-grid">
                    {filteredFiles.map(file => (
                        <MediaCard
                            key={file.uuid}
                            file={file}
                            selected={multiSelect
                                ? isAttached(file)
                                : file.id?.toString() === selectedValue?.toString()}
                            attached={isAttached(file)}
                            selectable={selectable}
                            onSelect={selectFile}
                            onEdit={openDetail}
                        />
                    ))}
                </div>
            )}

            {cropFile && <ImageCropEditor file={cropFile} files={files} preset={cropControls?.preset}
                onCancel={() => setCropFile(null)} onSave={file => {
                    setFiles(current => [file, ...current]);
                    setCropFile(null);
                    cropControls?.onSave?.(file);
                }} />}
            {(detail || detailLoading || detailError) && (
                <MediaDetailsPanel
                    detail={detail}
                    loading={detailLoading}
                    error={detailError}
                    selectable={selectable}
                    attached={detail?.file ? isAttached(detail.file) : false}
                    onCrop={cropControls && !multiSelect ? file => setCropFile(file) : undefined}
                    onClose={() => setDetail(null)}
                    onSelect={selectFile}
                    onSave={async values => {
                        const nextFile = await api.update(detail.file.uuid, values);
                        setFiles(current => updateFile(current, nextFile));
                        setDetail(current => ({...current, file: nextFile}));
                    }}
                    onRotate={async () => {
                        const nextFile = await api.rotate(detail.file.uuid);
                        setFiles(current => updateFile(current, nextFile));
                        setDetail(current => ({...current, file: nextFile}));
                    }}
                    onDelete={async () => {
                        try {
                            await api.delete(detail.file.uuid);
                        } catch (deleteError) {
                            if (deleteError.data?.attachmentCount) {
                                setDetail(current => ({
                                    ...current,
                                    attachments: deleteError.data.attachments || [],
                                    attachmentCount: deleteError.data.attachmentCount,
                                }));
                            }
                            throw deleteError;
                        }
                        setFiles(current => current.filter(file => file.uuid !== detail.file.uuid));
                        setDetail(null);
                        onDelete?.(detail.file);
                    }}
                />
            )}
        </div>
    );

    if (variant === 'modal') {
        return (
            <div className="media-system-modal-backdrop" role="presentation">
                <div role="dialog" aria-modal="true" aria-label={title}>
                    {panel}
                </div>
            </div>
        );
    }
    return panel;
}
